import React, { useEffect, useState } from 'react';
import './RecordingControls.css';
import { isFeatureEnabled } from '../features';
import MiniSlider from './RecordingVolumeSlider';
import RecordingVolumeMeter from './RecordingVolumeMeter';
import { Button, Space } from 'antd';
import {
  AudioOutlined,
  PauseOutlined,
  SaveOutlined,
  CopyOutlined,
  PlusOutlined,
  StopOutlined,
  BulbOutlined,
  BulbFilled,
} from '@ant-design/icons';

type RecordingControlsProps = {
  currentRecVolume: number;
  inputSignalStrengthRef: React.MutableRefObject<number>;
  recording?: boolean;
  playing?: boolean;
  started?: boolean;
  stopped?: boolean;
  saved?: boolean;
  saving?: boolean;

  onStart: () => void;
  onStop: () => void;
  onPause: () => void;
  onResume: () => void;
  onSave: () => void;
  onDownload: () => void;
  onRestart: () => void;
  onRecordingVolumeChange: (volume: number) => void;

  darkMode?: boolean;
  onToggleDarkMode?: () => void;

  restricted?: boolean;
};

function RecordingControls(props: RecordingControlsProps) {
  const [isSkinnyWindow, setIsSkinnyWindow] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsSkinnyWindow(window.matchMedia('(max-width: 960px)').matches);
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    return function cleanup() {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if (isFeatureEnabled('FAVICON_RECORDING_ICON')) {
    const faviconLink = document.getElementsByTagName('link');
    const normalIcon = 'favicon.ico';
    const recordingIcon = 'recording_favicon.ico';
    const fileString = faviconLink[0].href;

    if (props.recording && faviconLink !== undefined && faviconLink !== null) {
      faviconLink[0].href = fileString.replace(normalIcon, recordingIcon);
    } else if (!props.recording) {
      faviconLink[0].href = fileString.replace(recordingIcon, normalIcon);
    }
  }

  const maybeContent = (content: string): string => (isSkinnyWindow ? '' : content);

  return (
    <div className='recording-controls'>
      <Space.Compact>
        {props.started ? (
          props.recording ? (
            <Button icon={<PauseOutlined />} onClick={props.onPause} title='Gera hlé'>
              {maybeContent('Hlé')}
            </Button>
          ) : (
            <Button icon={<AudioOutlined />} onClick={props.onResume} disabled={props.stopped} title='Halda áfram'>
              {props.stopped ? null : maybeContent('Halda áfram')}
            </Button>
          )
        ) : (
          <Button icon={<AudioOutlined />} onClick={props.onStart} content={maybeContent('Byrja')} title='Byrja' />
        )}

        {!props.restricted ? (
          <Button
            title='Ljúka'
            icon={<StopOutlined />}
            onClick={props.onStop}
            disabled={props.recording || !props.started || props.stopped || props.saving}
          />
        ) : null}
        <Button
          title='Vista'
          icon={<SaveOutlined />}
          loading={props.saving}
          onClick={props.onSave}
          disabled={(!props.restricted && !props.stopped) || props.saving}
        >
          {!props.restricted && (!props.stopped || props.saved || props.saving) ? null : maybeContent('Vista')}
        </Button>
        {!props.restricted ? (
          <>
            <Button title='Afrita' icon={<CopyOutlined />} onClick={props.onDownload}>
              {maybeContent('Afrita')}
            </Button>

            <Button
              title='Hefja nýtt'
              icon={<PlusOutlined />}
              disabled={props.recording || props.saving}
              onClick={props.onRestart}
            >
              {!props.stopped || !props.saved || props.saving ? null : maybeContent('Hefja nýtt')}
            </Button>
          </>
        ) : null}
      </Space.Compact>
      {props.onToggleDarkMode ? (
        <Button
          className='dark-mode-toggle'
          title={props.darkMode ? 'Ljóshamur' : 'Myrkrahamur'}
          icon={props.darkMode ? <BulbFilled /> : <BulbOutlined />}
          onClick={props.onToggleDarkMode}
        />
      ) : null}
      <div id='rec-input-controls-container'>
        <MiniSlider
          minVal={0}
          maxVal={100}
          val={props.currentRecVolume}
          onChange={props.onRecordingVolumeChange}
          vertical
        />
        <RecordingVolumeMeter signalStrengthRef={props.inputSignalStrengthRef} />
      </div>
    </div>
  );
}

export default React.memo(RecordingControls);
